import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  Tabs,
  Tab,
  Typography,
  Box,
  Button,
  CircularProgress,
  Chip
} from "@material-ui/core";
import { Payment, CardPayment } from "@mercadopago/sdk-react";
import { toast } from "react-toastify";

import CheckoutSuccess from "./CheckoutSuccess";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { ensureMercadoPago } from "../../services/mercadoPago";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";

// Tradução amigável dos motivos de recusa do Mercado Pago
// (status_detail → chave em checkout.statusDetail)
const STATUS_DETAIL_MESSAGES = {
  cc_rejected_insufficient_amount: "insufficientAmount",
  cc_rejected_bad_filled_card_number: "badCardNumber",
  cc_rejected_bad_filled_date: "badDate",
  cc_rejected_bad_filled_security_code: "badSecurityCode",
  cc_rejected_bad_filled_other: "badOther",
  cc_rejected_call_for_authorize: "callForAuthorize",
  cc_rejected_card_disabled: "cardDisabled",
  cc_rejected_duplicated_payment: "duplicatedPayment",
  cc_rejected_high_risk: "highRisk",
  cc_rejected_max_attempts: "maxAttempts",
  cc_rejected_other_reason: "otherReason"
};

export const translateStatusDetail = (statusDetail) =>
  i18n.t(
    `checkout.statusDetail.${
      STATUS_DETAIL_MESSAGES[statusDetail] || "default"
    }`
  );

// Erros de validação de campo do Brick (type: "non_critical"): o Brick já
// mostra a mensagem embaixo do campo, aqui só reforçamos com um toast.
// (cause do Brick → chave em checkout.brickErrors)
const BRICK_ERROR_MESSAGES = {
  card_number_empty: "cardNumberEmpty",
  card_number_incomplete: "cardNumberIncomplete",
  card_number_invalid_type: "cardNumberInvalid",
  invalid_card_number: "cardNumberInvalid",
  expiration_date_empty: "expirationDateEmpty",
  expiration_date_incomplete: "expirationDateIncomplete",
  expiration_date_invalid_type: "expirationDateInvalid",
  invalid_expiry_date: "expirationDateInvalid",
  security_code_empty: "securityCodeEmpty",
  security_code_incomplete: "securityCodeIncomplete",
  security_code_invalid_type: "securityCodeInvalid",
  cardholder_name_empty: "cardholderNameEmpty",
  email_empty: "emailEmpty",
  email_invalid: "emailInvalid",
  identification_number_empty: "identificationNumberEmpty",
  identification_number_invalid: "identificationNumberInvalid",
  identification_type_empty: "identificationTypeEmpty",
  payment_method_not_found: "paymentMethodNotFound",
  installments_empty: "installmentsEmpty"
};

export const translateBrickError = (error) =>
  i18n.t(
    `checkout.brickErrors.${
      BRICK_ERROR_MESSAGES[error?.cause] ||
      (error?.type === "critical" ? "critical" : "generic")
    }`
  );

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("pt-br", {
    style: "currency",
    currency: "BRL"
  });

// Constantes de módulo: o SDK do MP destrói e recria o Brick sempre que a
// identidade de initialization/customization/callbacks muda entre renders —
// objetos inline causavam a corrida "container_not_found" e o form em branco.
const ONE_TIME_CUSTOMIZATION = {
  paymentMethods: {
    bankTransfer: "all",
    creditCard: "all",
    maxInstallments: 1
  }
};

const CARD_CUSTOMIZATION = {
  paymentMethods: { maxInstallments: 1 }
};

export default function CheckoutPage(props) {
  // initialTab: 0 = pagar fatura (avulso), 1 = assinatura automática
  const initialTab =
    props.initialTab !== undefined ? props.initialTab : props.Invoice?.id ? 0 : 1;

  const { user } = useContext(AuthContext);

  const [tab, setTab] = useState(initialTab);
  // Brick bloqueado por adblock/proteção de rastreamento → fallback sem Brick
  const [brickFailed, setBrickFailed] = useState(false);
  const [invoice, setInvoice] = useState(
    props.Invoice?.id ? props.Invoice : null
  );
  const [loadingInvoice, setLoadingInvoice] = useState(!props.Invoice?.id);
  const [invoiceError, setInvoiceError] = useState(false);
  const [pixData, setPixData] = useState(null);
  // Pix recusado pelo Mercado Pago (conta recebedora sem chave Pix cadastrada):
  // o toast some em segundos, então o aviso fica fixo orientando o cartão.
  const [pixUnavailable, setPixUnavailable] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [companyPlan, setCompanyPlan] = useState(null);
  const [monthlyValue, setMonthlyValue] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [processing, setProcessing] = useState(false);

  const mpReady = ensureMercadoPago();
  const hasInvoice = !!invoice?.id;

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const { data } = await api.get("/subscription/status");
        setSubscription(data.subscription);
        setCompanyPlan(data.company?.plan || null);
        setMonthlyValue(data.company?.monthlyValue ?? null);
      } catch (err) {
        toastError(err);
      } finally {
        setLoadingStatus(false);
      }
    };
    fetchStatus();
  }, []);

  // aberto sem fatura: garante uma fatura do ciclo atual para pagamento avulso
  useEffect(() => {
    if (props.Invoice?.id) return;
    const ensureInvoice = async () => {
      try {
        const { data } = await api.post("/subscription/renewal-invoice");
        setInvoice(data);
      } catch (err) {
        setInvoiceError(true);
        toastError(err);
      } finally {
        setLoadingInvoice(false);
      }
    };
    ensureInvoice();
  }, [props.Invoice]);

  const invoiceId = invoice?.id;
  // fatura de pró-rata de add-on não renova a licença — libera os add-ons
  const isAddonInvoice = invoice?.type === "addon_prorata";
  // recargas de crédito (template e IA) também não renovam a licença
  const isTemplateCreditInvoice = invoice?.type === "template_credit";
  const isAiCreditInvoice = invoice?.type === "ai_credit";
  const subscriptionActive = subscription?.status === "active";

  // O checkout é reaproveitado para quatro finalidades diferentes — título e
  // descrição precisam dizer o que o cliente está pagando de fato.
  const pageTitle = isTemplateCreditInvoice
    ? i18n.t("checkout.title.templateCredit")
    : isAiCreditInvoice
    ? i18n.t("checkout.title.aiCredit")
    : isAddonInvoice
    ? i18n.t("checkout.title.addon")
    : i18n.t("checkout.title.renewal");
  const paymentHint = isTemplateCreditInvoice
    ? i18n.t("checkout.hint.templateCredit")
    : isAiCreditInvoice
    ? i18n.t("checkout.hint.aiCredit")
    : isAddonInvoice
    ? i18n.t("checkout.hint.addon")
    : i18n.t("checkout.hint.renewal");
  // assinatura mensal só faz sentido para a fatura da licença
  const showSubscriptionTab =
    !isTemplateCreditInvoice && !isAiCreditInvoice && !isAddonInvoice;
  const activeTab = showSubscriptionTab ? tab : 0;
  // mensalidade composta (plano base + add-ons + personalização)
  const subscriptionAmount =
    monthlyValue ?? companyPlan?.value ?? subscription?.amount;

  // initialization/callbacks memoizados: o Brick só deve remontar se o
  // valor realmente mudar (ver constantes de customização no topo)
  const paymentInitialization = useMemo(
    () => ({ amount: Number(invoice?.value || 0) }),
    [invoice?.value]
  );
  const cardInitialization = useMemo(
    () => ({ amount: Number(subscriptionAmount || 0) }),
    [subscriptionAmount]
  );

  // O Brick chama onError tanto quando não consegue carregar (adblock) quanto
  // a cada erro de validação de campo. Só o primeiro caso pode trocar a tela —
  // trocar por cartão inválido faria o usuário perder o que já digitou.
  // Referência estável: os callbacks não podem mudar de identidade entre
  // renders (ver comentário das constantes de customização), por isso o
  // "já renderizou" vive num ref e não no state.
  const brickReadyRef = useRef(false);

  const handleBrickReady = useCallback(() => {
    brickReadyRef.current = true;
  }, []);

  const handleBrickError = useCallback((error) => {
    console.error("[MercadoPago] Brick error", error);

    // erro crítico antes do formulário aparecer = Brick bloqueado/não carregou
    if (error?.type === "critical" && !brickReadyRef.current) {
      setBrickFailed(true);
      return;
    }

    // formulário já na tela: apenas avisa, mantendo os dados digitados
    toast.error(translateBrickError(error), {
      toastId: `mp-brick-${error?.cause || "error"}`
    });
  }, []);

  // o SDK remonta o Brick quando o valor ou a aba muda: volta ao estado
  // "ainda não renderizou" para que um bloqueio de carregamento seja detectado
  useEffect(() => {
    brickReadyRef.current = false;
  }, [paymentInitialization, cardInitialization, activeTab]);

  // ==== Pagamento avulso (Payment Brick: pix + cartão) ====
  // O Brick só reabilita o botão "Pagar" quando a Promise do onSubmit é
  // rejeitada: resolvida, ele entende que o pagamento terminou e trava o botão.
  // Por isso toda falha que permite nova tentativa precisa sair daqui como erro.
  const handleOneTimeSubmit = useCallback(async ({ formData }) => {
    setProcessing(true);
    let data;
    try {
      ({ data } = await api.post("/subscription/payment", {
        invoiceId,
        formData
      }));
    } catch (err) {
      if (err.response?.data?.error === "ERR_MP_PIX_UNAVAILABLE") {
        setPixUnavailable(true);
      }
      toastError(err);
      throw err;
    } finally {
      setProcessing(false);
    }

    if (data.status === "approved") {
      toast.success(
        isTemplateCreditInvoice
          ? i18n.t("checkout.toasts.approvedTemplateCredit")
          : isAiCreditInvoice
          ? i18n.t("checkout.toasts.approvedAiCredit")
          : isAddonInvoice
          ? i18n.t("checkout.toasts.approvedAddon")
          : i18n.t("checkout.toasts.approvedRenewal")
      );
      setTimeout(() => {
        window.location.href = "/financeiro";
      }, 2500);
    } else if (
      data.status === "pending" &&
      data.point_of_interaction?.transaction_data?.qr_code
    ) {
      setPixData({
        qr_code: data.point_of_interaction.transaction_data.qr_code,
        qr_code_base64:
          data.point_of_interaction.transaction_data.qr_code_base64,
        amount: data.transaction_amount
      });
    } else if (data.status === "rejected") {
      toast.error(translateStatusDetail(data.status_detail));
      throw new Error(data.status_detail || "rejected");
    } else {
      // Em análise: o botão fica travado de propósito — tentar de novo aqui
      // cobraria duas vezes quando a primeira for aprovada.
      toast.info(i18n.t("checkout.toasts.processing"));
    }
  }, [invoiceId]);

  // ==== Assinatura (CardPayment Brick tokeniza o cartão) ====
  const handleSubscriptionSubmit = useCallback(async (formData) => {
    setProcessing(true);
    try {
      await api.post("/subscription/preapproval", {
        cardTokenId: formData.token,
        payerEmail: formData.payer?.email
      });
      toast.success(i18n.t("checkout.toasts.subscriptionActivated"));
      setTimeout(() => {
        window.location.href = "/financeiro";
      }, 2500);
    } catch (err) {
      toastError(err);
      throw err; // reabilita o botão do Brick (ver handleOneTimeSubmit)
    } finally {
      setProcessing(false);
    }
  }, []);

  // ==== Fallbacks sem Brick (adblock / proteção de rastreamento) ====
  const handleDirectPix = useCallback(async () => {
    try {
      await handleOneTimeSubmit({
        formData: {
          payment_method_id: "pix",
          payer: { email: user?.email }
        }
      });
    } catch (err) {
      // o toast já saiu; a rejeição existe só para o Brick, que aqui não há
    }
  }, [handleOneTimeSubmit, user]);

  // Pagamento com cartão acontece sempre dentro do painel (Brick) — nunca
  // redirecionamos o cliente para o site do Mercado Pago.
  // Pix recusado na conta recebedora: o cartão continua funcionando, então o
  // aviso muda conforme o formulário de cartão estar ou não disponível na tela.
  const pixUnavailableNotice = (
    <Box mb={2} px={1}>
      <Typography variant="body2" color="error" align="center">
        {brickFailed
          ? i18n.t("checkout.pixUnavailableSupport")
          : i18n.t("checkout.pixUnavailableCard")}
      </Typography>
    </Box>
  );

  const brickBlockedNotice = (
    <Typography variant="body2" color="textSecondary" gutterBottom>
      {i18n.t("checkout.brickBlocked")}
    </Typography>
  );

  if (!mpReady) {
    return (
      <Box p={3} textAlign="center">
        <Typography variant="h6">{i18n.t("checkout.unavailableTitle")}</Typography>
        <Typography variant="body2" color="textSecondary">
          {i18n.t("checkout.missingPublicKey")}
        </Typography>
      </Box>
    );
  }

  if (pixData) {
    return <CheckoutSuccess pix={pixData} />;
  }

  return (
    <Box minWidth={420}>
      <Typography component="h1" variant="h5" align="center" gutterBottom>
        {pageTitle}
      </Typography>

      {/* recarga de créditos e pró-rata de add-on não têm assinatura mensal */}
      {showSubscriptionTab && (
        <Tabs
          value={activeTab}
          onChange={(e, newTab) => setTab(newTab)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          style={{ marginBottom: 16 }}
        >
          <Tab
            label={i18n.t("checkout.tabs.payInvoice")}
            disabled={!hasInvoice && !loadingInvoice}
          />
          <Tab label={i18n.t("checkout.tabs.autoSubscription")} />
        </Tabs>
      )}

      {processing && (
        <Box display="flex" justifyContent="center" my={2}>
          <CircularProgress size={28} />
        </Box>
      )}

      {activeTab === 0 && loadingInvoice && (
        <Box display="flex" justifyContent="center" my={3}>
          <CircularProgress size={28} />
        </Box>
      )}

      {activeTab === 0 && !loadingInvoice && !hasInvoice && (
        <Box textAlign="center" p={2}>
          <Typography variant="body2" color="textSecondary">
            {invoiceError
              ? i18n.t("checkout.invoiceError")
              : i18n.t("checkout.noOpenInvoice")}
          </Typography>
        </Box>
      )}

      {activeTab === 0 && hasInvoice && (
        <>
          <Box mb={2} textAlign="center">
            <Typography variant="body2" color="textSecondary">
              {i18n.t("checkout.invoiceDue", {
                detail: invoice.detail,
                date: invoice.dueDate
                  ? new Date(
                      String(invoice.dueDate).substring(0, 10) + "T12:00:00"
                    ).toLocaleDateString("pt-br")
                  : ""
              })}
            </Typography>
            <Typography variant="h6">{formatCurrency(invoice.value)}</Typography>
            <Typography variant="body2" color="textSecondary">
              {paymentHint}
            </Typography>
          </Box>
          {pixUnavailable && pixUnavailableNotice}
          {brickFailed ? (
            <Box textAlign="center" p={1}>
              {brickBlockedNotice}
              <Box
                display="flex"
                flexDirection="column"
                gridGap={8}
                mt={2}
                maxWidth={340}
                mx="auto"
              >
                <Button
                  variant="contained"
                  color="primary"
                  disabled={processing}
                  onClick={handleDirectPix}
                >
                  {i18n.t("checkout.payWithPix")}
                </Button>
              </Box>
            </Box>
          ) : (
            <Payment
              key={`invoice-${invoiceId}`}
              initialization={paymentInitialization}
              customization={ONE_TIME_CUSTOMIZATION}
              onSubmit={handleOneTimeSubmit}
              onReady={handleBrickReady}
              onError={handleBrickError}
            />
          )}
        </>
      )}

      {activeTab === 1 && (
        <>
          {loadingStatus ? (
            <Box display="flex" justifyContent="center" my={3}>
              <CircularProgress size={28} />
            </Box>
          ) : subscriptionActive ? (
            <Box p={2} textAlign="center">
              <Chip
                label={i18n.t("checkout.subscriptionActiveChip")}
                color="primary"
              />
              <Typography variant="body2" style={{ marginTop: 12 }}>
                {subscription?.payerEmail
                  ? i18n.t("checkout.subscriptionAlreadyActiveEmail", {
                      email: subscription.payerEmail
                    })
                  : i18n.t("checkout.subscriptionAlreadyActive")}
              </Typography>
            </Box>
          ) : (
            <>
              <Box mb={2} textAlign="center">
                <Typography variant="body2" color="textSecondary">
                  {companyPlan?.name
                    ? i18n.t("checkout.autoDebitPlan", { plan: companyPlan.name })
                    : i18n.t("checkout.autoDebit")}
                </Typography>
                <Typography variant="h6">
                  {i18n.t("checkout.perMonth", {
                    value: formatCurrency(subscriptionAmount)
                  })}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {i18n.t("checkout.autoRenewInfo")}
                </Typography>
              </Box>
              {subscriptionAmount > 0 ? (
                brickFailed ? (
                  <Box textAlign="center" p={1} maxWidth={380} mx="auto">
                    {brickBlockedNotice}
                    <Typography variant="caption" color="textSecondary">
                      {i18n.t("checkout.payPixMeanwhile", {
                        tab: i18n.t("checkout.tabs.payInvoice")
                      })}
                    </Typography>
                  </Box>
                ) : (
                  <CardPayment
                    initialization={cardInitialization}
                    customization={CARD_CUSTOMIZATION}
                    onSubmit={handleSubscriptionSubmit}
                    onReady={handleBrickReady}
                    onError={handleBrickError}
                  />
                )
              ) : (
                <Typography variant="body2" align="center" color="error">
                  {i18n.t("checkout.noPlanValue")}
                </Typography>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
}
